import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiPlus, FiEdit2, FiTrash2, FiSearch,
  FiToggleLeft, FiToggleRight, FiMoreVertical, FiTruck,
  FiX, FiLock
} from 'react-icons/fi';
import { ManagerLayout } from '../../components/layout';
import { Card, Button, Loading, AlertModal, ConfirmDialog } from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { getCarsByCompany, toggleCarStatus, deleteCar, createCarBlock, getCarBlocks, deleteCarBlock } from '../../firebase/cars';

const CarList = () => {
  const { managerCompanyId, isAdmin, user } = useAuth();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [selectedCar, setSelectedCar] = useState(null);
  const [blockData, setBlockData] = useState({
    fromDate: '',
    fromTime: '10',
    toDate: '',
    toTime: '10',
    reason: 'Offline booking',
    type: 'offline_booking'
  });
  const [blockErrors, setBlockErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [carBlocks, setCarBlocks] = useState({});
  const [alert, setAlert] = useState({ isOpen: false, type: 'success', title: '', message: '' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, message: '', onConfirm: null, title: '', type: 'warning' });
  const [carToDelete, setCarToDelete] = useState(null);
  const [blockToDelete, setBlockToDelete] = useState(null);

  useEffect(() => {
    if (managerCompanyId || isAdmin) {
      loadCars();
    }
  }, [managerCompanyId, isAdmin]);

  const loadCars = async () => {
    if (!managerCompanyId && !isAdmin) return;
    
    setLoading(true);
    try {
      const result = await getCarsByCompany(managerCompanyId, { includeInactive: true });
      setCars(result.cars || []);
    } catch (error) {
      console.error('Error loading cars:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (car) => {
    const newStatus = car.status === 'active' ? 'inactive' : 'active';
    const result = await toggleCarStatus(car.id, newStatus);
    
    if (!result.error) {
      setCars(cars.map(c => 
        c.id === car.id ? { ...c, status: newStatus } : c
      ));
    }
    setActiveDropdown(null);
  };

  const handleDelete = (car) => {
    setCarToDelete(car);
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Car',
      message: `Are you sure you want to delete "${car.name}"? This action cannot be undone.`,
      type: 'danger',
      onConfirm: async () => {
        const result = await deleteCar(car.id);
        if (!result.error) {
          setCars(cars.filter(c => c.id !== car.id));
          setAlert({
            isOpen: true,
            type: 'success',
            title: 'Car Deleted',
            message: `"${car.name}" has been deleted successfully.`
          });
        } else {
          setAlert({
            isOpen: true,
            type: 'error',
            title: 'Delete Failed',
            message: result.error || 'Failed to delete car. Please try again.'
          });
        }
        setCarToDelete(null);
        setActiveDropdown(null);
      }
    });
  };

  const handleOpenBlockModal = async (car) => {
    setSelectedCar(car);
    setShowBlockModal(true);
    setBlockData({
      fromDate: '',
      fromTime: '10',
      toDate: '',
      toTime: '10',
      reason: 'Offline booking',
      type: 'offline_booking'
    });
    setBlockErrors({});
    
    // Load existing blocks for this car
    const blocksResult = await getCarBlocks(car.id);
    setCarBlocks(prev => ({ ...prev, [car.id]: blocksResult.blocks || [] }));
  };

  const handleBlockChange = (e) => {
    const { name, value } = e.target;
    setBlockData(prev => ({ ...prev, [name]: value }));
    if (blockErrors[name]) {
      setBlockErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateBlock = () => {
    const errors = {};
    
    if (!blockData.fromDate) errors.fromDate = 'Start date is required';
    if (!blockData.fromTime) errors.fromTime = 'Start time is required';
    if (!blockData.toDate) errors.toDate = 'End date is required';
    if (!blockData.toTime) errors.toTime = 'End time is required';
    
    if (blockData.fromDate && blockData.toDate && blockData.fromTime && blockData.toTime) {
      const fromDateTime = `${blockData.fromDate}T${blockData.fromTime}:00`;
      const toDateTime = `${blockData.toDate}T${blockData.toTime}:00`;
      const from = new Date(fromDateTime);
      const to = new Date(toDateTime);
      if (to <= from) {
        errors.toDate = 'End date/time must be after start date/time';
      }
    }
    
    setBlockErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Convert date and time to IST timezone string
  const convertToIST = (date, time) => {
    if (!date || !time) return '';
    // Format: YYYY-MM-DDTHH:00:00+05:30
    return `${date}T${time.padStart(2, '0')}:00:00+05:30`;
  };

  const handleCreateBlock = async (e) => {
    e.preventDefault();
    
    if (!validateBlock()) return;
    
    if (!managerCompanyId) {
      setAlert({
        isOpen: true,
        type: 'error',
        title: 'Company Not Found',
        message: 'Company ID not found. Please ensure you are assigned to a company.'
      });
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Convert date and time to IST timezone strings
      const fromDateTimeIST = convertToIST(blockData.fromDate, blockData.fromTime);
      const toDateTimeIST = convertToIST(blockData.toDate, blockData.toTime);
      
      const result = await createCarBlock(selectedCar.id, managerCompanyId, {
        fromDateTime: fromDateTimeIST,
        toDateTime: toDateTimeIST,
        reason: blockData.reason,
        type: blockData.type,
        createdBy: user?.uid || 'manager'
      });
      
      if (result.error) {
        console.error('Block creation error:', result.error);
        setAlert({
          isOpen: true,
          type: 'error',
          title: 'Block Failed',
          message: `Failed to create block: ${result.error}`
        });
      } else {
        setAlert({
          isOpen: true,
          type: 'success',
          title: 'Car Blocked',
          message: 'Car blocked successfully!'
        });
        setShowBlockModal(false);
        // Reload blocks
        const blocksResult = await getCarBlocks(selectedCar.id);
        setCarBlocks(prev => ({ ...prev, [selectedCar.id]: blocksResult.blocks || [] }));
      }
    } catch (error) {
      console.error('Error creating block:', error);
      setAlert({
        isOpen: true,
        type: 'error',
        title: 'Block Failed',
        message: `Failed to create block: ${error.message || error}`
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteBlock = (blockId, carId) => {
    setBlockToDelete({ blockId, carId });
    setConfirmDialog({
      isOpen: true,
      title: 'Remove Block',
      message: 'Are you sure you want to remove this block?',
      type: 'warning',
      onConfirm: async () => {
        try {
          const result = await deleteCarBlock(blockId);
          if (result.error) {
            setAlert({
              isOpen: true,
              type: 'error',
              title: 'Delete Failed',
              message: result.error || 'Failed to delete block. Please try again.'
            });
          } else {
            // Reload blocks
            const blocksResult = await getCarBlocks(carId);
            setCarBlocks(prev => ({ ...prev, [carId]: blocksResult.blocks || [] }));
            setAlert({
              isOpen: true,
              type: 'success',
              title: 'Block Removed',
              message: 'Car block has been removed successfully.'
            });
          }
        } catch (error) {
          console.error('Error deleting block:', error);
          setAlert({
            isOpen: true,
            type: 'error',
            title: 'Delete Failed',
            message: 'Failed to delete block. Please try again.'
          });
        }
        setBlockToDelete(null);
      }
    });
  };

  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return 'N/A';
    const date = new Date(dateTimeStr);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter cars
  const filteredCars = cars.filter(car => {
    const matchesSearch = car.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      car.brand?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      car.model?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || car.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <ManagerLayout title="Cars">
        <div className="flex items-center justify-center h-64">
          <Loading />
        </div>
      </ManagerLayout>
    );
  }

  return (
    <ManagerLayout title="Cars">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Your Cars</h2>
          <p className="text-gray-600">{cars.length} total cars</p>
        </div>
        <Link to="/manager/cars/new">
          <Button className="flex items-center gap-2">
            <FiPlus size={18} />
            Add Car
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search by name, brand, or model..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="maintenance">Maintenance</option>
          </select>
        </div>
      </Card>

      {/* Cars Grid */}
      {filteredCars.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCars.map((car) => (
            <Card key={car.id} className="overflow-hidden">
              {/* Car Image */}
              <div className="aspect-video bg-gray-100 relative">
                {car.images?.[0] ? (
                  <img 
                    src={car.images[0]} 
                    alt={car.name} 
                    className="w-full h-full object-contain bg-gray-100"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <FiTruck size={48} className="text-gray-300" />
                  </div>
                )}
                <span className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium ${
                  car.status === 'active' 
                    ? 'bg-green-100 text-green-700' 
                    : car.status === 'maintenance'
                    ? 'bg-yellow-100 text-yellow-700'
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {car.status}
                </span>
              </div>
              
              {/* Car Info */}
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-semibold text-gray-900">{car.name}</h3>
                    <p className="text-sm text-gray-500">
                      {car.brand} {car.model && `• ${car.model}`} {car.year && `• ${car.year}`}
                    </p>
                  </div>
                  
                  {/* Actions Dropdown */}
                  <div className="relative">
                    <button
                      onClick={() => setActiveDropdown(activeDropdown === car.id ? null : car.id)}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <FiMoreVertical size={18} />
                    </button>
                    
                    {activeDropdown === car.id && (
                      <>
                        <div 
                          className="fixed inset-0 z-10" 
                          onClick={() => setActiveDropdown(null)}
                        />
                        <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                          <Link
                            to={`/manager/cars/${car.id}`}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            <FiEdit2 size={14} />
                            Edit
                          </Link>
                          <button
                            onClick={() => handleOpenBlockModal(car)}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full"
                          >
                            <FiLock size={14} />
                            Block Car
                          </button>
                          <button
                            onClick={() => handleToggleStatus(car)}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full"
                          >
                            {car.status === 'active' ? (
                              <>
                                <FiToggleLeft size={14} />
                                Deactivate
                              </>
                            ) : (
                              <>
                                <FiToggleRight size={14} />
                                Activate
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => handleDelete(car)}
                            className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full"
                          >
                            <FiTrash2 size={14} />
                            Delete
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Specs */}
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-600">
                    {car.seats} Seats
                  </span>
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-600">
                    {car.transmission}
                  </span>
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-600">
                    {car.fuelType}
                  </span>
                </div>
                
                {/* Pricing */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <div>
                    <p className="text-xs text-gray-500">12hr</p>
                    <p className="font-semibold text-gray-900">₹{car.price12Hour?.toLocaleString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">24hr</p>
                    <p className="font-semibold text-gray-900">₹{car.price24Hour?.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="p-12 text-center">
          <div className="text-gray-400 mb-4">
            <FiTruck size={48} className="mx-auto" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {searchQuery || statusFilter !== 'all' ? 'No cars found' : 'No cars yet'}
          </h3>
          <p className="text-gray-600 mb-4">
            {searchQuery || statusFilter !== 'all' 
              ? 'Try adjusting your search or filters'
              : 'Start by adding your first car'
            }
          </p>
          {!searchQuery && statusFilter === 'all' && (
            <Link to="/manager/cars/new">
              <Button>Add First Car</Button>
            </Link>
          )}
        </Card>
      )}

      {/* Block Car Modal */}
      {showBlockModal && selectedCar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Block Car</h3>
                  <p className="text-sm text-gray-500 mt-1">{selectedCar.name}</p>
                </div>
                <button
                  onClick={() => {
                    setShowBlockModal(false);
                    setSelectedCar(null);
                    setBlockErrors({});
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX size={24} />
                </button>
              </div>
              
              <form onSubmit={handleCreateBlock} className="space-y-4">
                {/* Start Date & Time */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    Start Date & Time *
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">
                        Date
                      </label>
                      <input
                        type="date"
                        name="fromDate"
                        value={blockData.fromDate}
                        onChange={handleBlockChange}
                        min={new Date().toISOString().split('T')[0]}
                        className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-gray-900 ${
                          blockErrors.fromDate ? 'border-red-500' : 'border-gray-200'
                        }`}
                      />
                      {blockErrors.fromDate && (
                        <p className="text-red-500 text-xs mt-1">{blockErrors.fromDate}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">
                        Time (Hour)
                      </label>
                      <select
                        name="fromTime"
                        value={blockData.fromTime}
                        onChange={handleBlockChange}
                        className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-gray-900 ${
                          blockErrors.fromTime ? 'border-red-500' : 'border-gray-200'
                        }`}
                      >
                        {Array.from({ length: 24 }, (_, i) => {
                          const hour = i;
                          const period = hour >= 12 ? 'PM' : 'AM';
                          const hour12 = hour % 12 || 12;
                          return (
                            <option key={hour} value={hour}>
                              {hour12}:00 {period}
                            </option>
                          );
                        })}
                      </select>
                      {blockErrors.fromTime && (
                        <p className="text-red-500 text-xs mt-1">{blockErrors.fromTime}</p>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* End Date & Time */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    End Date & Time *
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">
                        Date
                      </label>
                      <input
                        type="date"
                        name="toDate"
                        value={blockData.toDate}
                        onChange={handleBlockChange}
                        min={blockData.fromDate || new Date().toISOString().split('T')[0]}
                        className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-gray-900 ${
                          blockErrors.toDate ? 'border-red-500' : 'border-gray-200'
                        }`}
                      />
                      {blockErrors.toDate && (
                        <p className="text-red-500 text-xs mt-1">{blockErrors.toDate}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">
                        Time (Hour)
                      </label>
                      <select
                        name="toTime"
                        value={blockData.toTime}
                        onChange={handleBlockChange}
                        className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-gray-900 ${
                          blockErrors.toTime ? 'border-red-500' : 'border-gray-200'
                        }`}
                      >
                        {Array.from({ length: 24 }, (_, i) => {
                          const hour = i;
                          const period = hour >= 12 ? 'PM' : 'AM';
                          const hour12 = hour % 12 || 12;
                          return (
                            <option key={hour} value={hour}>
                              {hour12}:00 {period}
                            </option>
                          );
                        })}
                      </select>
                      {blockErrors.toTime && (
                        <p className="text-red-500 text-xs mt-1">{blockErrors.toTime}</p>
                      )}
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason
                  </label>
                  <input
                    type="text"
                    name="reason"
                    value={blockData.reason}
                    onChange={handleBlockChange}
                    placeholder="e.g., Offline booking, Maintenance"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Block Type
                  </label>
                  <select
                    name="type"
                    value={blockData.type}
                    onChange={handleBlockChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="offline_booking">Offline Booking</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                {/* Existing Blocks */}
                {carBlocks[selectedCar.id] && carBlocks[selectedCar.id].length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Existing Blocks
                    </label>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {carBlocks[selectedCar.id].map((block) => (
                        <div key={block.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{block.reason}</p>
                            <p className="text-xs text-gray-500">
                              {formatDateTime(block.fromDateTime)} - {formatDateTime(block.toDateTime)}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleDeleteBlock(block.id, selectedCar.id)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                            title="Remove block"
                          >
                            <FiX size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowBlockModal(false);
                      setSelectedCar(null);
                      setBlockErrors({});
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="flex items-center gap-2"
                  >
                    {submitting ? <Loading size="sm" /> : <FiLock size={18} />}
                    Block Car
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}
      {/* Alert Modal */}
      <AlertModal
        isOpen={alert.isOpen}
        onClose={() => setAlert({ ...alert, isOpen: false })}
        type={alert.type}
        title={alert.title}
        message={alert.message}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
      />
    </ManagerLayout>
  );
};

export default CarList;
